import * as React from 'react';
import "./pageSelectionComponent.scss"
import * as classNames from 'classnames';
import _ = require("underscore");

interface ComponentProps {
	/**
	 * All items number.
	 */
	itemsNumber: number;
	/**
	 * Number of items displayed on single page.
	 */
	itemsPerPage: number;
	/**
	 * Page index change callback
	 * @param {number} value
	 */
	onChange?: (value: number) => void;
	/**
	 * CurrentPageIndex
	 */
	currentPageIndex: number;
}

interface State {
	/**
	 * Currently displayed page index.
	 */
	currentPageIndex: number;
	/**
	 * Number of all pages.
	 */
	pagesNumber: number;
	/**
	 * Currently displayed index of first page to choose.
	 */
	firstIndexToSelect: number;
	/**
	 * Currently displayed index of last page to choose.
	 */
	lastIndexToSelect: number;
}

type Props = ComponentProps;

/**
 * Page selection component.
 */
export default class PageSelectionComponent extends React.Component<Props, State> {

	/**
	 * Number of displayed selectable page indexes.
	 * @type {number}
	 */
	private pageIndexesToShowNumber = 5;

	constructor(props: Props) {
		super(props);
		const pagesNumber = Math.ceil(props.itemsNumber / props.itemsPerPage);
		this.state = this.recalculateState(pagesNumber, props.currentPageIndex);
		this.onPageChange = this.onPageChange.bind(this);
		this.onPrevPage = this.onPrevPage.bind(this);
		this.onNextPage = this.onNextPage.bind(this);
	}

	/**
	 * A list can show a paginator above and below the rows; a page picked in one reaches the other through
	 * currentPageIndex, so a change of it has to move the displayed page too.
	 */
	componentWillReceiveProps(nextProps: Props) {
		const pagesNumber = Math.ceil(nextProps.itemsNumber / nextProps.itemsPerPage);
		if (this.state.pagesNumber != pagesNumber || this.props.currentPageIndex != nextProps.currentPageIndex) {
			this.setState(this.recalculateState(pagesNumber, nextProps.currentPageIndex));
		}
	}

	recalculateState(pagesNumber: number, currentPageIndex: number) {
		return {
			currentPageIndex: currentPageIndex,
			pagesNumber,
			...this.indexesToSelect(currentPageIndex, pagesNumber)
		}
	}

	/**
	 * Selectable page indexes window, kept centred on the given page.
	 * @param {number} index
	 * @param {number} pagesNumber
	 */
	indexesToSelect(index: number, pagesNumber: number) {
		const distanceToCenter = Math.floor(this.pageIndexesToShowNumber / 2);
		if (pagesNumber <= this.pageIndexesToShowNumber || index - distanceToCenter <= 1) {
			return {
				firstIndexToSelect: 1,
				lastIndexToSelect: Math.min(pagesNumber, this.pageIndexesToShowNumber)
			};
		}
		if (index + distanceToCenter >= pagesNumber) {
			return {
				firstIndexToSelect: pagesNumber - (this.pageIndexesToShowNumber - 1),
				lastIndexToSelect: pagesNumber
			};
		}
		return {
			firstIndexToSelect: index - distanceToCenter,
			lastIndexToSelect: index + distanceToCenter
		};
	}


	render() {
		const firstDisplayedRecordIndex = this.props.itemsPerPage * (this.state.currentPageIndex - 1) + 1;
		const lastPageLastRecordIndex = firstDisplayedRecordIndex + this.props.itemsPerPage - 1;
		const lastDisplayedRecordIndex = lastPageLastRecordIndex > this.props.itemsNumber ?
			this.props.itemsNumber :
			lastPageLastRecordIndex;
		const componentText = classNames({
			"c-page-selection-component_text": true,
			"long-list": this.state.pagesNumber > 1
		});
		return (
			<div className="c-page-selection-component">
				<div className={componentText}>
					{firstDisplayedRecordIndex} - {lastDisplayedRecordIndex} <span
					className="c-page-selection-component_text_normal-weight">of</span> {this.props.itemsNumber}
				</div>
				<div className="c-page-selection-component_navigation-container">
					{this.state.currentPageIndex > 1 ?
						<div className="c-page-selection-component_arrow-wrapper" onClick={this.onPrevPage}>
							<div className="c-page-selection-component_arrow-left"/>
						</div> :
						this.state.pagesNumber > 1 ? <div className="c-page-selection-component_empty-left"/> : null}
					{this.state.pagesNumber > 1 &&
					_.range(this.state.firstIndexToSelect, this.state.lastIndexToSelect + 1).map(index => {
						const classFromState = classNames({
							"c-page-selection-component_selectable-index": true,
							"selected": index === this.state.currentPageIndex
						});
						return (
							<div key={index} className={classFromState} onClick={() => this.onPageChange(index)}>{index}</div>
						);
					})}
					{this.state.currentPageIndex < this.state.pagesNumber ?
						<div className="c-page-selection-component_arrow-wrapper" onClick={this.onNextPage}>
							<div className="c-page-selection-component_arrow-right"/>
						</div> :
						this.state.pagesNumber > 1 ? <div className="c-page-selection-component_empty-right"/> : null}
				</div>
			</div>
		);
	}

	/**
	 * Page index click callback.
	 * @param {number} index
	 */
	onPageChange(index: number) {
		this.setState({currentPageIndex: index, ...this.indexesToSelect(index, this.state.pagesNumber)},
			() => this.props.onChange && this.props.onChange(index));
	}

	/**
	 * Previous page click callback.
	 */
	onPrevPage() {
		const prevPageIndex = this.state.currentPageIndex - 1;
		if (prevPageIndex > 0) {
			this.onPageChange(prevPageIndex);
		}
	}

	/**
	 * Next page click callback.
	 */
	onNextPage() {
		const nextPageIndex = this.state.currentPageIndex + 1;
		if (nextPageIndex <= this.state.pagesNumber) {
			this.onPageChange(nextPageIndex);
		}
	}
}
